<template>
  <md-dialog ref="markerDialog" class="script-marker-dialog">
    <md-dialog-title>{{ dialogTitle }}</md-dialog-title>
    <md-dialog-content>
      <md-input-container class="md-has-select md-has-value">
        <label>Type</label>
        <select class="md-select md-theme-default" v-model="markerType" style="border: none;">
          <option class="md-list-item md-menu-item md-option" value="act">Act</option>
          <option class="md-list-item md-menu-item md-option" value="scene">Scene</option>
          <option class="md-list-item md-menu-item md-option" value="song">Song</option>
        </select>
      </md-input-container>
      <md-input-container>
        <label>Label</label>
        <md-input v-model="markerLabel" placeholder="Scene 1" />
      </md-input-container>
      <md-input-container v-if="totalPages > 1" class="md-has-select md-has-value">
        <label>Page</label>
        <select class="md-select md-theme-default" v-model.number="markerPageIndex" style="border: none;">
          <option
            v-for="pageNum in totalPages"
            :key="pageNum"
            class="md-list-item md-menu-item md-option"
            :value="pageNum"
          >{{ pageLabel(pageNum) }}</option>
        </select>
      </md-input-container>
      <p class="marker-page-hint">{{ hintText }}</p>
    </md-dialog-content>
    <md-dialog-actions>
      <md-button class="md-primary" @click="save">{{ saveLabel }}</md-button>
      <md-button @click="closeDialog">Cancel</md-button>
    </md-dialog-actions>
  </md-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
    name: 'ScriptMarkerDialog',
    props: {
        currentPage: { type: Number, required: true },
        pageWidth: { type: Number, default: 0 },
        pageHeight: { type: Number, default: 0 },
        totalPages: { type: Number, default: 0 }
    },
    data() {
        return {
            mode: 'add',
            markerType: 'scene',
            markerLabel: '',
            markerPageIndex: 1,
            editingMarker: null
        };
    },
    computed: {
        ...mapGetters(['scriptMarkersByPage', 'project']),
        dialogTitle() {
            return this.mode === 'edit' ? 'Edit script marker' : 'Add script marker';
        },
        saveLabel() {
            return this.mode === 'edit' ? 'Save' : 'Add';
        },
        hintText() {
            if (this.mode === 'edit') {
                return 'Drag the marker on the script to reposition.';
            }
            return `Placed on ${this.pageLabel(this.markerPageIndex || this.currentPage)}. Drag markers on the script to reposition.`;
        }
    },
    methods: {
        ...mapActions(['addScriptMarker', 'updateScriptMarker']),
        pageLabel(pageNum) {
            const pages = this.project && this.project.pdfPageNumbers;
            if (pages && pages[pageNum - 1] && pages[pageNum - 1].name) {
                return `Page ${pages[pageNum - 1].name}`;
            }
            return `Page ${pageNum}`;
        },
        openAdd() {
            this.mode = 'add';
            this.editingMarker = null;
            this.markerLabel = '';
            this.markerType = 'scene';
            this.markerPageIndex = this.currentPage;
            this.$refs.markerDialog.open();
        },
        openEdit(marker) {
            this.mode = 'edit';
            this.editingMarker = marker;
            this.markerLabel = marker.label || '';
            this.markerType = marker.type || 'scene';
            this.markerPageIndex = marker.pageIndex || this.currentPage;
            this.$refs.markerDialog.open();
        },
        closeDialog() {
            this.$refs.markerDialog.close();
        },
        async save() {
            if (!this.markerLabel.trim()) {
                return;
            }
            if (this.mode === 'add') {
                await this.saveNewMarker();
            } else {
                await this.saveEditedMarker();
            }
            this.closeDialog();
            this.$emit('saved');
        },
        async saveNewMarker() {
            // Cascade against marks already on the TARGET page, not the page the
            // user happens to be viewing, or a mark added to another page stacks
            // on top of whatever is there.
            //
            // scriptMarkersByPage is deliberately the UNFILTERED getter: a user
            // who has hidden structure still needs new marks to cascade past the
            // ones they cannot see, or every one lands at the same y.
            const targetPage = this.markerPageIndex || this.currentPage;
            const existing = this.scriptMarkersByPage(targetPage) || [];
            let x = 40;
            let y = 120 + (existing.length * 28);
            if (this.pageWidth > 0 && this.pageHeight > 0) {
                x /= this.pageWidth;
                if (x > 1) x = 1;
                y /= this.pageHeight;
                if (y > 1) y = 1;
            }
            await this.addScriptMarker({
                pageIndex: targetPage,
                x,
                y,
                type: this.markerType,
                label: this.markerLabel.trim()
            });
        },
        async saveEditedMarker() {
            const marker = this.editingMarker;
            if (!marker) {
                return;
            }
            try {
                await this.updateScriptMarker({
                    ...marker,
                    markerId: marker.markerId || marker.id,
                    pageIndex: this.markerPageIndex,
                    type: this.markerType,
                    label: this.markerLabel.trim()
                });
            } catch (err) {
                // Conflict toast is handled in marker-actions.
            }
        }
    }
};
</script>

<style scoped>
.marker-page-hint {
  font-size: 12px;
  color: #666;
  margin-top: 8px;
}
</style>


// WEBPACK FOOTER //
// marker-dialog.vue?3e5969d0